// Packages
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import DatePicker from "react-datepicker";
// Components
import ModalCloseIcon from "../modalCloseIcon";
import SelectInput from "../selectInput";
// Helpers, Utils etc.
import { findUnschedulableItems } from "../../helpers/categorySchedule";
import { translatedName } from "../../helpers/translations";
import {
  findNextWorkingDay,
  getAvailableTimeSlots,
  workingHoursForDay,
} from "../../helpers/timeCalculations";

interface IStoreClosedOrderSchedulePopupMobileProps {
  onClose: any;
  for: string;
  defaultEta: any;
  openingHours: any;
  confirmScheduleDate: any;
  minimumEtaMinutes: any;
  hasLeadTime: boolean;
  cart: any;
  menu: any;
}

const StoreClosedOrderSchedulePopupMobile: React.FC<
  IStoreClosedOrderSchedulePopupMobileProps
> = ({
  onClose,
  for: orderType,
  defaultEta,
  openingHours,
  confirmScheduleDate,
  minimumEtaMinutes,
  hasLeadTime,
  cart,
  menu,
}) => {
  const intl = useIntl();

  const minDate = findNextWorkingDay(
    new Date(),
    openingHours,
    minimumEtaMinutes
  ); // Next store open date
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 1); // One month from today

  // States for date and time selection
  const [selectedDate, setSelectedDate] = useState<Date>(minDate);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [availableTimes, setAvailableTimes] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [showSchedule, setShowSchedule] = useState(true);
  const [unschedulableItems, setUnschedulableItems] = useState<any[]>([]);

  // Filter available dates based on working hours
  const filterAvailableDates = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    const workingHours = workingHoursForDay(dayOfWeek, openingHours);

    // Check if the day has any working hours
    return workingHours && workingHours.length > 0;
  };

  // Check if selected date and time are valid
  const checkForUnschedulableItems = (date: Date, time: string) => {
    const currentTime = new Date();
    const expectedMinutes = currentTime.getMinutes() + minimumEtaMinutes;
    currentTime.setMinutes(expectedMinutes);

    // Create a date object with selected date and time
    const selectedDateTime = new Date(date);
    const [timeStr, period] = time.split(" ");
    const [hours, minutes] = timeStr.split(":");
    let hour = parseInt(hours);
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;
    selectedDateTime.setHours(hour, parseInt(minutes));

    const scheduledTime = new Date(selectedDateTime.getTime());
    if (hasLeadTime || selectedDateTime.getTime() > currentTime.getTime()) {
      scheduledTime.setMinutes(
        scheduledTime.getMinutes() - parseInt(defaultEta)
      );
      const itemsThatCantBeScheduled = findUnschedulableItems(
        scheduledTime,
        cart.items,
        menu.categories
      );
      setUnschedulableItems(itemsThatCantBeScheduled);
    } else {
      setUnschedulableItems([]);
    }
  };

  const handleClose = () => {
    setTimeout(() => {
      onClose();
    }, 500);
    setShowSchedule(false);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Create a date object with selected date and time
    const [timeStr, period] = selectedTime.split(" ");
    const [hours, minutes] = timeStr.split(":");
    let hour = parseInt(hours);
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    const scheduledDateTime = new Date(selectedDate);
    scheduledDateTime.setHours(hour, parseInt(minutes));

    confirmScheduleDate(scheduledDateTime, selectedTime);
    handleClose();
  };

  const handleDateChange = (date: Date) => {
    if (date) {
      setSelectedDate(date);
      const times = getAvailableTimeSlots(
        date,
        openingHours,
        defaultEta,
        minimumEtaMinutes
      );
      setAvailableTimes(times);
      setSelectedTime(times[0]?.value || "");
      checkForUnschedulableItems(date, times[0]?.value || "");
    }
  };

  const handleTimeChange = (time: string) => {
    setSelectedTime(time);
    checkForUnschedulableItems(selectedDate, time);
  };

  // Initialize with current date
  useEffect(() => {
    handleDateChange(minDate);
  }, []);

  return (
    <Modal
      show={showSchedule}
      centered
      dialogClassName="custom curved"
      backdrop="static"
    >
      <ModalCloseIcon onClick={handleClose} />
      <div className="schedule-orders-popup">
        <div className="header">
          {orderType === "Delivery" ? (
            <FormattedMessage
              id="schedule.delivery"
              defaultMessage="Schedule Delivery"
            />
          ) : (
            <FormattedMessage
              id="schedule.pickup"
              defaultMessage="Schedule Pickup"
            />
          )}
        </div>

        {unschedulableItems.length > 0 && (
          <div className="schedule-orders-warning">
            <div className="warning-header">
              <FormattedMessage id="error.oops" defaultMessage="Oops!" />
            </div>
            <div className="warning-body">
              <p>
                <FormattedMessage
                  id="error.order_cannot_be_scheduled"
                  defaultMessage="We're sorry for the inconvenience, but your order includes items that cannot be scheduled for the selected date and time:"
                />
              </p>

              <ul>
                {unschedulableItems.map((item: any, index: number) => (
                  <li key={index}>{translatedName(item, intl.locale)}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <form className="form" onReset={handleClose} onSubmit={handleSubmit}>
          <div className="d-flex mb-4">
            <div className="form-group mb-2 w-100 mr-3">
              <label className="input-label custom bold mb-3">
                {orderType === "Delivery" ? (
                  <FormattedMessage
                    id="schedule.delivery_date"
                    defaultMessage="Delivery Date"
                  />
                ) : (
                  <FormattedMessage
                    id="schedule.pickup_date"
                    defaultMessage="Pickup Date"
                  />
                )}
              </label>
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                minDate={minDate}
                maxDate={maxDate}
                filterDate={filterAvailableDates}
                dateFormat="MMMM d, yyyy"
                className="form-control custom-datepicker-mobile"
                placeholderText="Select a date"
                calendarClassName="custom-datepicker-calendar"
                showPopperArrow={false}
                excludeDates={[]}
                inline={false}
                style={{ height: "50px" }}
                required
              />
            </div>
            <div className="form-group mb-2 w-100">
              <label className="input-label custom bold mb-3">
                {orderType === "Delivery" ? (
                  <FormattedMessage
                    id="schedule.delivery_time"
                    defaultMessage="Delivery Time"
                  />
                ) : (
                  <FormattedMessage
                    id="schedule.pickup_time"
                    defaultMessage="Pickup Time"
                  />
                )}
              </label>
              <SelectInput
                options={availableTimes}
                defaultValue={selectedTime}
                onChange={handleTimeChange}
                fullWidth
              />
            </div>
          </div>

          <div className="actions">
            <button
              className="button btn btn-outline-primary mr-3 w-100"
              type="reset"
            >
              <FormattedMessage id="global.back" defaultMessage="Back" />
            </button>
            <button
              className="button btn btn-primary w-100"
              type="submit"
              disabled={!selectedTime || unschedulableItems.length > 0}
            >
              <FormattedMessage id="global.submit" defaultMessage="Submit" />
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  cart: state.cart,
  menu: state.menu,
});

export default connect(mapStateToProps)(StoreClosedOrderSchedulePopupMobile);
